import { useContext, useState } from 'react';
import { Outlet, Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ColorModeContext } from '../../app/theme';
import { UserRole, hasRole } from '../../types/user';
import {
  AppBar,
  Box,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ArticleIcon from '@mui/icons-material/Article';
import WorkIcon from '@mui/icons-material/Work';
import ImageIcon from '@mui/icons-material/Image';
import MailIcon from '@mui/icons-material/Mail';
import LogoutIcon from '@mui/icons-material/Logout';
import HomeIcon from '@mui/icons-material/Home';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PeopleIcon from '@mui/icons-material/People';
import SecurityIcon from '@mui/icons-material/Security';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import MenuIcon from '@mui/icons-material/Menu';

const DRAWER_WIDTH = 240;

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { mode, toggle } = useContext(ColorModeContext);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    await logout();
    localStorage.removeItem('odc_admin_access');
    navigate('/login');
  };

  const adminNav = [
    { label: 'Dashboard', path: '/admin', icon: <DashboardIcon />, requiredRole: UserRole.User },
    { label: 'Profile', path: '/admin/profile', icon: <PersonIcon />, requiredRole: UserRole.User },
    { label: 'Users', path: '/admin/users', icon: <PeopleIcon />, requiredRole: UserRole.Admin },
    { label: 'Roles', path: '/admin/roles', icon: <SecurityIcon />, requiredRole: UserRole.Admin },
    { label: 'Blog', path: '/admin/blog', icon: <ArticleIcon />, requiredRole: UserRole.Admin },
    { label: 'Projects', path: '/admin/projects', icon: <WorkIcon />, requiredRole: UserRole.Admin },
    { label: 'Media', path: '/admin/media', icon: <ImageIcon />, requiredRole: UserRole.Admin },
    { label: 'Messages', path: '/admin/messages', icon: <MailIcon />, requiredRole: UserRole.SuperAdmin },
    { label: 'Mailing List', path: '/admin/mailing-list', icon: <EmailIcon />, requiredRole: UserRole.SuperAdmin },
  ];

  const drawerContent = (
    <>
      <Toolbar />
      <Box sx={{ overflow: 'auto' }}>
        <List>
          {adminNav.filter(item => hasRole(user?.roles, item.requiredRole)).map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to={item.path}
                  selected={isActive}
                  onClick={isMobile ? handleDrawerToggle : undefined}
                  sx={{
                    '&.Mui-selected': {
                      bgcolor: 'action.selected',
                      borderRight: '3px solid',
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
        <Divider />
        <Box sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Logged in as {user?.roles?.join(', ') || 'User'}
          </Typography>
        </Box>
      </Box>
    </>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Top App Bar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {isMobile ? 'Admin' : 'Admin Panel'}
          </Typography>
          {!isMobile && (
            <Typography variant="body2" sx={{ mr: 2 }}>
              {user?.firstName ? `${user.firstName} ${user.lastName || ''}` : user?.email}
            </Typography>
          )}
          {isMobile ? (
            <>
              <IconButton
                color="inherit"
                component={RouterLink}
                to="/"
                sx={{ mr: 0.5 }}
              >
                <HomeIcon />
              </IconButton>
              <IconButton
                color="inherit"
                onClick={handleLogout}
                sx={{ mr: 0.5 }}
              >
                <LogoutIcon />
              </IconButton>
              <IconButton onClick={toggle} color="inherit">
                {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
              </IconButton>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                color="inherit"
                component={RouterLink}
                to="/"
                startIcon={<HomeIcon />}
                sx={{
                  mr: 1,
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                View Site
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleLogout}
                startIcon={<LogoutIcon />}
                sx={{
                  mr: 1,
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                Logout
              </Button>
              <IconButton onClick={toggle} color="inherit">
                {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
              </IconButton>
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* Side Navigation Drawer */}
      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        {/* Mobile Drawer (Temporary) */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
            },
          }}
        >
          {drawerContent}
        </Drawer>

        {/* Desktop Drawer (Permanent) */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { xs: '100%', md: `calc(100% - ${DRAWER_WIDTH}px)` },
          bgcolor: (theme) => theme.palette.mode === 'light' ? 'grey.50' : 'background.default',
          minHeight: '100vh',
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
}

